import type { NexusEvent, NexusRelation, NodeKind, NodeStatus, OrbitalKind } from "../domain/mesh";
import { de } from "./catalogs/de";
import { en } from "./catalogs/en";
import { pl } from "./catalogs/pl";
import type { Locale, LocaleCatalog } from "./types";

export type { Locale, EntityCopy, EventCopy, LocaleCatalog, UiCopy } from "./types";

export const localeLabels: Record<Locale, string> = { en: "EN", pl: "PL", de: "DE" };
export const localeFormats: Record<Locale, string> = { en: "en-GB", pl: "pl-PL", de: "de-DE" };
export const catalogs: Record<Locale, LocaleCatalog> = { en, pl, de };
const relativeTimeFormatters = new Map<Locale, Intl.RelativeTimeFormat>();

function getRelativeTimeFormatter(locale: Locale) {
  const existing = relativeTimeFormatters.get(locale);
  if (existing) return existing;
  const formatter = new Intl.RelativeTimeFormat(localeFormats[locale], { numeric: "auto" });
  relativeTimeFormatters.set(locale, formatter);
  return formatter;
}

export function getCatalog(locale: Locale) {
  return catalogs[locale];
}

export function formatEvent(event: NexusEvent, locale: Locale) {
  return catalogs[locale].events[event.type](event);
}

export function formatTime(date: Date | string | number, locale: Locale) {
  const value = typeof date === "string" || typeof date === "number" ? new Date(date) : date;
  return value.toLocaleTimeString(localeFormats[locale], {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function formatShortTime(date: string, locale: Locale) {
  return new Date(date).toLocaleTimeString(localeFormats[locale], { hour: "2-digit", minute: "2-digit" });
}

export function formatDate(date: Date, locale: Locale) {
  return date
    .toLocaleDateString(localeFormats[locale], { day: "2-digit", month: "short", year: "numeric" })
    .toUpperCase();
}

export function timeAgo(iso: string | undefined, locale: Locale) {
  if (!iso) return catalogs[locale].ui.unknown;
  const deltaSeconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const formatter = getRelativeTimeFormatter(locale);
  const absoluteSeconds = Math.abs(deltaSeconds);
  if (absoluteSeconds < 60) return formatter.format(deltaSeconds, "second");
  const deltaMinutes = Math.round(deltaSeconds / 60);
  if (Math.abs(deltaMinutes) < 60) return formatter.format(deltaMinutes, "minute");
  const deltaHours = Math.round(deltaMinutes / 60);
  if (Math.abs(deltaHours) < 48) return formatter.format(deltaHours, "hour");
  return formatter.format(Math.round(deltaHours / 24), "day");
}

export function statusLabel(status: NodeStatus, locale: Locale) {
  const map: Record<Locale, Record<NodeStatus, string>> = {
    en: {
      online: "ONLINE",
      offline: "OFFLINE",
      active: "ACTIVE",
      building: "BUILDING",
      draft: "DRAFT",
      warning: "WARNING",
    },
    pl: {
      online: "ONLINE",
      offline: "OFFLINE",
      active: "AKTYWNY",
      building: "BUDOWA",
      draft: "SZKIC",
      warning: "OSTRZEŻENIE",
    },
    de: {
      online: "ONLINE",
      offline: "OFFLINE",
      active: "AKTIV",
      building: "IM BAU",
      draft: "ENTWURF",
      warning: "WARNUNG",
    },
  };
  return map[locale][status];
}

export function kindLabel(kind: NodeKind | OrbitalKind, locale: Locale) {
  const map: Record<Locale, Record<NodeKind | OrbitalKind, string>> = {
    en: {
      system: "SYSTEM",
      entity: "ENTITY",
      artifact: "ARTIFACT",
      location: "LOCATION",
      goal: "GOAL",
      service: "SERVICE",
      runtime: "RUNTIME",
      storage: "STORAGE",
      integration: "INTEGRATION",
      library: "LIBRARY",
    },
    pl: {
      system: "SYSTEM",
      entity: "ENCJA",
      artifact: "ARTEFAKT",
      location: "LOKALIZACJA",
      goal: "CEL",
      service: "USŁUGA",
      runtime: "RUNTIME",
      storage: "MAGAZYN",
      integration: "INTEGRACJA",
      library: "BIBLIOTEKA",
    },
    de: {
      system: "SYSTEM",
      entity: "ENTITÄT",
      artifact: "ARTEFAKT",
      location: "ORT",
      goal: "ZIEL",
      service: "SERVICE",
      runtime: "RUNTIME",
      storage: "SPEICHER",
      integration: "INTEGRATION",
      library: "BIBLIOTHEK",
    },
  };
  return map[locale][kind];
}

export function relationLabel(relation: NexusRelation, locale: Locale) {
  const map: Record<Locale, Record<NexusRelation, string>> = {
    en: {
      depends_on: "DEPENDS ON",
      presents: "PRESENTS",
      tracks: "TRACKS",
      contains: "CONTAINS",
      relates_to: "RELATES TO",
      drives: "DRIVES",
      located_at: "LOCATED AT",
    },
    pl: {
      depends_on: "ZALEŻY OD",
      presents: "PREZENTUJE",
      tracks: "ŚLEDZI",
      contains: "ZAWIERA",
      relates_to: "POWIĄZANE Z",
      drives: "NAPĘDZA",
      located_at: "ZLOKALIZOWANE W",
    },
    de: {
      depends_on: "HÄNGT AB VON",
      presents: "PRÄSENTIERT",
      tracks: "VERFOLGT",
      contains: "ENTHÄLT",
      relates_to: "BEZIEHT SICH AUF",
      drives: "TREIBT",
      located_at: "BEFINDET SICH IN",
    },
  };
  return map[locale][relation];
}

export function relationPerspectiveLabel(relation: NexusRelation, outgoing: boolean, locale: Locale) {
  if (outgoing) return `${relationLabel(relation, locale)} →`;
  const inverse: Record<Locale, Record<NexusRelation, string>> = {
    en: {
      depends_on: "← REQUIRED BY",
      presents: "← PRESENTED BY",
      tracks: "← TRACKED BY",
      contains: "← CONTAINED BY",
      relates_to: "← RELATED TO",
      drives: "← DRIVEN BY",
      located_at: "← LOCATION OF",
    },
    pl: {
      depends_on: "← WYMAGANE PRZEZ",
      presents: "← PREZENTOWANE PRZEZ",
      tracks: "← ŚLEDZONE PRZEZ",
      contains: "← ZAWARTE W",
      relates_to: "← POWIĄZANE Z",
      drives: "← NAPĘDZANE PRZEZ",
      located_at: "← LOKALIZACJA DLA",
    },
    de: {
      depends_on: "← BENÖTIGT VON",
      presents: "← PRÄSENTIERT VON",
      tracks: "← VERFOLGT VON",
      contains: "← ENTHALTEN IN",
      relates_to: "← BEZOGEN AUF",
      drives: "← ANGETRIEBEN VON",
      located_at: "← STANDORT VON",
    },
  };
  return inverse[locale][relation];
}

export function resolveInitialLocale(language = typeof navigator === "undefined" ? "en" : navigator.language): Locale {
  const normalized = language.toLowerCase();
  if (normalized.startsWith("pl")) return "pl";
  if (normalized.startsWith("de")) return "de";
  return "en";
}
